import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FilterSingleSelect } from "@/components/common/FilterSingleSelect";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCustomPages,
  useDeleteCustomPage,
  useUpdateCustomPage,
} from "./hooks/useCustomPages";
import { CustomPage, CustomPageStatusFilter } from "@/types/custom-page";
import { CustomPagesTable } from "./components/CustomPagesTable";

const ITEMS_PER_PAGE = 10;

export const CustomPagesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] =
    useState<CustomPageStatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageToDelete, setPageToDelete] = useState<CustomPage | null>(null);
  const { data: customPages = [], isLoading } = useCustomPages();
  const updateCustomPage = useUpdateCustomPage();
  const deleteCustomPage = useDeleteCustomPage();

  const filteredPages = useMemo(() => {
    return customPages.filter((page) => {
      if (statusFilter === "enabled") return page.enabled;
      if (statusFilter === "disabled") return !page.enabled;
      return true;
    });
  }, [customPages, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPages.length / ITEMS_PER_PAGE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedPages = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPages.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, filteredPages]);

  const handleFilterChange = (value: string) => {
    setStatusFilter(value as CustomPageStatusFilter);
    setCurrentPage(1);
  };

  const handleToggleEnabled = async (page: CustomPage, enabled: boolean) => {
    await updateCustomPage.mutateAsync({
      id: page.id,
      data: { enabled },
    });
  };

  const handleDelete = async (page: CustomPage) => {
    setPageToDelete(page);
  };

  const handleConfirmDelete = async () => {
    if (!pageToDelete) return;

    await deleteCustomPage.mutateAsync(pageToDelete.id);
    setPageToDelete(null);
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col space-y-6 p-12">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-zinc-900">Custom Pages</h1>
          <p className="text-zinc-500">
            Manage guest-facing HTML pages for the mobile app.
          </p>
        </div>

        <Button
          variant="new_default"
          onClick={() => navigate("/custom-pages/new")}
        >
          <Plus />
          Add page
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <FilterSingleSelect
          value={statusFilter}
          onValueChange={handleFilterChange}
          placeholder="Filter by status"
          className="w-[220px] justify-between"
          options={[
            { value: "all", label: "All pages" },
            { value: "enabled", label: "Enabled only" },
            { value: "disabled", label: "Disabled only" },
          ]}
        />
      </div>

      <CustomPagesTable
        pages={paginatedPages}
        currentPage={currentPage}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setCurrentPage}
        onToggleEnabled={handleToggleEnabled}
        onDelete={handleDelete}
        isUpdating={updateCustomPage.isPending || deleteCustomPage.isPending}
      />

      <Dialog
        open={!!pageToDelete}
        onOpenChange={(open) => !open && setPageToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete custom page?</DialogTitle>
            <DialogDescription>
              {pageToDelete
                ? `Delete "${pageToDelete.title}"? This action cannot be undone.`
                : "This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="new_outline"
                disabled={deleteCustomPage.isPending}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="new_destructive"
              onClick={handleConfirmDelete}
              disabled={deleteCustomPage.isPending}
            >
              {deleteCustomPage.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
